import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

export const useTimelineStore = create((set, get) => ({
  // 轨道数据
  tracks: [
    {
      id: 'offense-track',
      name: '进攻轨',
      type: 'offense',
      color: '#4CAF50',
      watermark: '进攻',
      clips: []
    },
    {
      id: 'defense-track', 
      name: '防守轨',
      type: 'defense',
      color: '#F44336',
      watermark: '防守',
      clips: []
    }
  ],

  // 原始视频文件路径（用于导出）
  videoFile: null,
  
  // 设置视频文件路径
  setVideoFile: (filePath) => set({ videoFile: filePath }),
  
  // 自定义轨道计数器
  customTrackCounter: 1,

  // 选中的轨道和片段
  selectedTrackId: null,
  selectedClipId: null,

  // 导出对话框状态
  showExportDialog: false,
  exportTrackId: null,

  // 添加片段到对应轨道
  addClip: (clipData) => {
    const { tracks } = get();
    const clipId = uuidv4();
    
    const newClip = {
      id: clipId,
      ...clipData,
      createdAt: new Date().toISOString()
    };

    const updatedTracks = tracks.map(track => {
      if (track.type === clipData.type) {
        return {
          ...track,
          clips: [...track.clips, newClip]
        };
      }
      return track;
    });

    set({ tracks: updatedTracks });
    return clipId;
  },

  // 创建新的自定义轨道
  createCustomTrack: (name) => {
    const { tracks, customTrackCounter } = get();
    const trackId = uuidv4();
    
    const newTrack = {
      id: trackId,
      name: name || `自定义轨道${customTrackCounter}`,
      type: 'custom',
      clips: [],
      color: '#2196F3',
      watermark: null
    };

    set({ 
      tracks: [...tracks, newTrack],
      customTrackCounter: customTrackCounter + 1
    });
    
    return trackId;
  },

  // 添加轨道（别名函数，与createCustomTrack功能相同）
  addTrack: (name) => {
    return get().createCustomTrack(name);
  },

  // 删除轨道
  deleteTrack: (trackId) => {
    const { tracks } = get();
    const updatedTracks = tracks.filter(track => track.id !== trackId);
    set({ tracks: updatedTracks });
  },

  // 重命名轨道
  renameTrack: (trackId, newName) => {
    const { tracks } = get();
    const updatedTracks = tracks.map(track => 
      track.id === trackId ? { ...track, name: newName } : track
    );
    set({ tracks: updatedTracks });
  },

  // 移动轨道顺序
  reorderTracks: (newTracks) => {
    set({ tracks: newTracks });
  },

  // 在轨道间移动片段
  moveClipToTrack: (clipId, fromTrackId, toTrackId, position = -1) => {
    const { tracks } = get();
    
    let clipToMove = null;
    const updatedTracks = tracks.map(track => {
      if (track.id === fromTrackId) {
        const clipIndex = track.clips.findIndex(clip => clip.id === clipId);
        if (clipIndex !== -1) {
          clipToMove = track.clips[clipIndex];
          return {
            ...track,
            clips: track.clips.filter(clip => clip.id !== clipId)
          };
        }
      }
      return track;
    });

    if (clipToMove) {
      const finalTracks = updatedTracks.map(track => {
        if (track.id === toTrackId) {
          const newClips = [...track.clips];
          if (position === -1) {
            newClips.push(clipToMove);
          } else {
            newClips.splice(position, 0, clipToMove);
          }
          return { ...track, clips: newClips };
        }
        return track;
      });
      
      set({ tracks: finalTracks });
    }
  },

  // 在同一轨道内重排片段
  reorderClipsInTrack: (trackId, newClips) => {
    const { tracks } = get();
    const updatedTracks = tracks.map(track => {
      if (track.id === trackId) {
        return { ...track, clips: newClips };
      }
      return track;
    });
    set({ tracks: updatedTracks });
  },

  // 删除片段
  deleteClip: (trackId, clipId) => {
    const { tracks } = get();
    const updatedTracks = tracks.map(track => {
      if (track.id === trackId) {
        return {
          ...track,
          clips: track.clips.filter(clip => clip.id !== clipId)
        };
      }
      return track;
    });
    set({ tracks: updatedTracks });
  },

  // 更新片段信息
  updateClip: (trackId, clipId, updates) => {
    const { tracks } = get();
    const updatedTracks = tracks.map(track => {
      if (track.id === trackId) {
        return {
          ...track,
          clips: track.clips.map(clip => 
            clip.id === clipId ? { ...clip, ...updates } : clip
          )
        };
      }
      return track;
    });
    set({ tracks: updatedTracks });
  },

  // 修剪片段时长
  trimClip: (trackId, clipId, newStartTime, newEndTime) => {
    const updates = {
      startTime: newStartTime,
      endTime: newEndTime,
      duration: newEndTime - newStartTime
    };
    get().updateClip(trackId, clipId, updates);
  },

  // 选择轨道
  selectTrack: (trackId) => set({ selectedTrackId: trackId }),

  // 选择片段
  selectClip: (clipId) => set({ selectedClipId: clipId }),

  // 获取轨道总时长
  getTrackDuration: (trackId) => {
    const { tracks } = get();
    const track = tracks.find(t => t.id === trackId);
    if (!track || track.clips.length === 0) return 0;
    
    return track.clips.reduce((total, clip) => total + clip.duration, 0);
  },

  // 获取轨道片段数量
  getTrackClipCount: (trackId) => {
    const { tracks } = get();
    const track = tracks.find(t => t.id === trackId);
    return track ? track.clips.length : 0;
  },

  // 显示导出对话框
  showExport: (trackId = null) => set({ 
    showExportDialog: true, 
    exportTrackId: trackId 
  }),

  // 隐藏导出对话框
  hideExport: () => set({ 
    showExportDialog: false, 
    exportTrackId: null 
  }),

  // 导出轨道
  exportTrack: (trackId, options = {}) => {
    // 这里会调用实际的导出逻辑
    console.log('导出轨道:', trackId, options);
    get().showExport(trackId);
  },

  // 清空所有轨道
  clearAllTracks: () => {
    const { tracks } = get();
    const clearedTracks = tracks.map(track => ({
      ...track,
      clips: []
    }));
    set({ tracks: clearedTracks });
  },

  // 重置到初始状态
  reset: () => set({
    tracks: [
      {
        id: 'offense-track',
        name: '进攻轨',
        type: 'offense',
        clips: [],
        color: '#4CAF50',
        watermark: '进攻'
      },
      {
        id: 'defense-track',
        name: '防守轨', 
        type: 'defense',
        clips: [],
        color: '#F44336',
        watermark: '防守'
      }
    ],
    customTrackCounter: 1,
    selectedTrackId: null,
    selectedClipId: null,
    showExportDialog: false,
    exportTrackId: null
  })
}));